import { useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'
import { RadioGroup } from '@headlessui/react'

export interface CustomRadioData {
  value: string | number
  img?: string
  name: string
  elements?: Record<string, string | number | React.ReactNode>
  endElement?: React.ReactNode
}

interface CustomRadioInterface {
  action?: (_value: string | number) => void
  className?: string
  data: CustomRadioData[]
  orientationHorizontal?: boolean
}

function classNames(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ')
}

export default function CustomRadio(
  props: UseControllerProps & CustomRadioInterface
) {
  const { action, className, data, orientationHorizontal } = props
  const { field } = useController(props)
  const [selected, setSelected] = useState<number | string | null>(null)

  const onChange = (value: string | number) => {
    setSelected(value)
    field.onChange(value)
    action?.(value)
  }

  return (
    <div className="p-2">
      <RadioGroup onChange={onChange} value={selected}>
        <div
          className={classNames(
            orientationHorizontal
              ? 'grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
              : 'flex flex-col gap-3 sm:gap-4'
          )}
        >
          {data?.map((item) => (
            <div
              className={classNames(
                'flex max-w-4xl gap-x-2',
                orientationHorizontal && 'w-full'
              )}
              key={item.value}
            >
              <RadioGroup.Option
                className={({ active, checked }) =>
                  classNames(
                    'grid items-center rounded-xl border bg-white p-4 text-sm shadow-sm sm:p-5',
                    'cursor-pointer transition-all duration-150 ease-in-out focus:outline-none hover:bg-gray-50',
                    checked
                      ? 'border-2 border-slate-600'
                      : 'border border-gray-300',
                    active && 'border-slate-600 ring-2 ring-slate-600',
                    `grid-cols-${item.elements ? Object.values(item.elements).length + 2 : 2}`,
                    className,
                    'w-full gap-3 sm:gap-4'
                  )
                }
                key={item.value}
                value={item.value}
              >
                {() => (
                  <>
                    <span className="col-span-2 inline-flex items-center gap-2 text-base text-gray-800 sm:text-sm">
                      {item.img && (
                        <img
                          alt={item.name}
                          className="h-10 w-10 rounded-full object-cover sm:h-8 sm:w-8"
                          src={item.img}
                        />
                      )}
                      {item.name}
                    </span>

                    {item.elements &&
                      Object.entries(item.elements).map(([key, elem], idx) => {
                        const isInput =
                          key.toLowerCase().includes('input') ||
                          key.toLowerCase().includes('quantity')

                        return (
                          <div
                            className={classNames(
                              'flex justify-center text-xs text-gray-600',
                              isInput &&
                                'col-span-2 w-40 sm:col-span-1 sm:w-auto'
                            )}
                            key={idx}
                          >
                            {typeof elem === 'string' ||
                            typeof elem === 'number' ? (
                              <span>{elem}</span>
                            ) : (
                              <>{elem}</>
                            )}
                          </div>
                        )
                      })}
                  </>
                )}
              </RadioGroup.Option>

              {item.endElement && (
                <div className="flex items-center justify-center">
                  {item.endElement}
                </div>
              )}
            </div>
          ))}
        </div>
      </RadioGroup>
    </div>
  )
}
